import type { UpgradeTrigger } from '@shared/core/analytics'
import type { BillingPlanDto } from '@shared/core/billing'
import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { TAG_MANAGER_EVENTS } from 'src/modules/core/types/analytics.types'
import { useStartShopifyCheckout } from 'src/modules/payment/hooks/use-shopify-billing'
import { isShopifyBilledProfile } from 'src/modules/payment/utils/helpers'
import { invalidateBillingQueries } from 'src/modules/payment/utils/invalidate-billing-queries'
import { waitForSubscription } from 'src/modules/payment/utils/wait-for-subscription'
import { useProfile } from 'src/modules/user/hooks/use-profile'

export type FreePlanCheckoutOptions = {
  trigger?: UpgradeTrigger
  isYearly: boolean
}

/** Buys a first subscription on the current page, with no calendar step. */
export function useFreePlanCheckout() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const profile = useProfile()
  const startShopifyCheckout = useStartShopifyCheckout()
  const [isAwaitingSubscription, setIsAwaitingSubscription] = useState(false)
  const { refetch } = profile
  const userId = profile.data?.id

  const handlePurchaseComplete = useCallback(
    async (previousSubscriptionId: number | null) => {
      window.dataLayer?.push({ event: TAG_MANAGER_EVENTS.PURCHASE_COMPLETE_ONBOARDING, user_id: userId })
      setIsAwaitingSubscription(true)
      await waitForSubscription(refetch, previousSubscriptionId)
      invalidateBillingQueries(queryClient)
      navigate(routeNames.dashboard.path)
    },
    [navigate, queryClient, refetch, userId],
  )

  const openCheckout = useCallback(
    (plan: BillingPlanDto, options: FreePlanCheckoutOptions) => {
      window.dataLayer?.push({ event: TAG_MANAGER_EVENTS.CHOOSE_SUBSCRIPTION_ONBOARDING, user_id: userId })

      if (isShopifyBilledProfile(profile.data)) {
        startShopifyCheckout.mutate(
          { paddle_plan_id: plan.paddle_id, is_yearly: options.isYearly },
          { onSuccess: ({ shopify_admin_url }) => window.top?.location.assign(shopify_admin_url) },
        )
        return
      }

      const previousSubscriptionId = profile.data?.billing_subscription_id ?? null
      window.Paddle.Checkout.open({
        product: plan.paddle_id,
        email: profile.data?.email,
        passthrough: JSON.stringify({ user_id: userId, trigger: options.trigger }),
        successCallback: () => handlePurchaseComplete(previousSubscriptionId),
      })
    },
    [handlePurchaseComplete, profile.data, startShopifyCheckout, userId],
  )

  return { openCheckout, isLoading: isAwaitingSubscription || startShopifyCheckout.isLoading }
}
